import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch } from '../app/hooks';
import { messagingActions } from '../reducers/messagingSlice';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import { selectFeatureFlagAgentCart } from '../redux/features/config/config.selector';
import { setLastAgentCartRequestId } from '../redux/features/aiSuggestions/aiSuggestions.slice';
import { selectCartItems, selectCartItemsQuantity } from '../selectors/cart';
import { selectIsExpressTimerActive } from '../selectors/menu';
import { IBrandMenuOrderItem } from '../types/brandMenu';
import { buildUnifiedMenuModuleOrder } from '../utils/menuUnification';

// Debounce window to coalesce rapid edits (e.g. selecting size then mods).
const AGENT_CART_DEBOUNCE_MS = 250;

/**
 * HITL-18: stream the human agent's working cart to the AI as an `agent_cart`
 * event whenever the cart changes during takeover (the co-pilot input side).
 *
 * Driver rule: emit only when the human is driving (isAIAutoMode === false) — in
 * AI auto-mode the AI builds the cart itself, so echoing it back would be
 * circular. Watching the cart selectors captures every cartSlice mutation, and
 * including isAIAutoMode in the deps means the first emit also fires at the
 * moment of interception (carrying the cart at takeover). Gated by the
 * FEATURE_FLAG_AGENT_CART feature flag.
 *
 * HITL-1805: once the express timer has latched for this car, every snapshot
 * carries `express_mode`, so the AI (which never sees the timer) stops
 * suggesting express-suppressed upsells. The latch is in the deps, so it sends
 * a snapshot at once instead of waiting for the agent's next edit. Before the
 * latch the payload has no `express_mode` key at all.
 */
const useAgentCartSync = () => {
  const dispatch = useAppDispatch();
  const isEnabled = useSelector(selectFeatureFlagAgentCart);
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const cartItems = useSelector(selectCartItems);
  const cartItemsQuantity = useSelector(selectCartItemsQuantity);
  const isExpressTimerActive = useSelector(selectIsExpressTimerActive);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!isEnabled || isAIAutoMode) {
      return;
    }
    timerRef.current = setTimeout(() => {
      // Full snapshot in the brand-menu-order shape the AI consumes. Includes
      // unresolved items (empty constraints) so the AI can decide the next TTS;
      // an emptied cart sends items: [].
      const items = Object.values(cartItems)
        .map((item) => buildUnifiedMenuModuleOrder(item, cartItemsQuantity))
        .filter((item): item is IBrandMenuOrderItem => Boolean(item));
      const requestId = uuidv4();
      dispatch(
        messagingActions.sendAgentCart({
          data: {
            cart_authority: 'human',
            final: false,
            request_id: requestId,
            items,
            ...(isExpressTimerActive && {
              express_mode: {
                active: true,
                reason: 'express_timer',
              },
            }),
          },
        })
      );
      // Remember the request_id to correlate (and drop stale) AI suggestions.
      dispatch(setLastAgentCartRequestId(requestId));
    }, AGENT_CART_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [
    cartItems,
    cartItemsQuantity,
    isAIAutoMode,
    isEnabled,
    isExpressTimerActive,
    dispatch,
  ]);
};

export default useAgentCartSync;
